import { z } from "zod";

// The vault keeps secrets an agent uses but never reads (ADR-023 §4). A secret has a name, the
// hosts its value may be sent to and the agents allowed to use it; inside the VM the agent holds
// only its placeholder (`zph_<name>`), and the egress proxy asks the resolve door for the value on
// the way out. The value goes in once, on save, and no schema here ever carries it back out.

/** An environment-variable-like name: `HUNTER_KEY`. */
export const vaultSecretNameSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z][A-Za-z0-9_]{0,63}$/, "letters, digits and underscores, starting with a letter");

/** A host a value may go to: a lower-case DNS name, no scheme, port or path (`api.hunter.io`). */
export const vaultHostSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(253)
  .regex(
    /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/,
    "a host name such as api.example.com, without https:// or a path",
  );

/** What an agent holds in place of the value. */
/** The shape the egress proxy matches (`zph_` and 1 to 64 of `[A-Za-z0-9_]`); the vault itself
 * only ever makes lower-case ones, so any other names no secret (`unknown_placeholder`). */
export const vaultPlaceholderSchema = z.string().regex(/^zph_[A-Za-z0-9_]{1,64}$/);

/** `secret`: a value the egress proxy swaps in for its hosts, or `vault run` releases.
 * `website-login`: a username, password and optional one-time-code seed for one https site, which
 * only the runtime types into that site's login form (ADR-023 §4, D657); it is never swapped and
 * never released. */
export const VAULT_SECRET_KINDS = ["secret", "website-login"] as const;
export const vaultSecretKindSchema = z.enum(VAULT_SECRET_KINDS);
export type VaultSecretKind = z.infer<typeof vaultSecretKindSchema>;

/** A website login's site: an https origin, lower-case, no path (`https://www.instagram.com`). */
export const vaultLoginOriginSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(300)
  .regex(
    /^https:\/\/(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?::\d{1,5})?$/,
    "an https address such as https://www.instagram.com, without a path",
  );

/** A one-time-code seed as authenticator apps show it: base32, spaces allowed. */
export const vaultTotpSeedSchema = z
  .string()
  .trim()
  .transform((value) => value.replace(/[\s-]/g, "").toUpperCase().replace(/=+$/, ""))
  .pipe(z.string().regex(/^[A-Z2-7]{16,128}$/, "the base32 key your authenticator app shows"));

/** What anyone may see about a secret: never its value, never its ciphertext. */
export const vaultSecretSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  kind: vaultSecretKindSchema,
  /** A website login's site; null for an API key. */
  loginOrigin: z.string().nullable(),
  placeholder: z.string(),
  allowedHosts: z.array(z.string()),
  allowedAgentIds: z.array(z.string()),
  createdByUserId: z.string(),
  createdAt: z.string(),
});
export type VaultSecret = z.infer<typeof vaultSecretSchema>;

export const saveVaultSecretInputSchema = z.object({
  name: vaultSecretNameSchema,
  value: z.string().min(1).max(8192),
  allowedHosts: z.array(vaultHostSchema).min(1).max(20),
  allowedAgentIds: z.array(z.string().min(1).max(64)).max(50),
});
export type SaveVaultSecretInput = z.infer<typeof saveVaultSecretInputSchema>;

/** A website login (D657): its site, username and password, and the seed of its one-time codes
 * when the site asks for one. Its only allowed host is the origin's. */
export const saveVaultLoginInputSchema = z.object({
  name: vaultSecretNameSchema,
  origin: vaultLoginOriginSchema,
  username: z.string().trim().min(1).max(512),
  password: z.string().min(1).max(1024),
  totpSeed: vaultTotpSeedSchema.optional(),
  allowedAgentIds: z.array(z.string().min(1).max(64)).max(50),
});
export type SaveVaultLoginInput = z.infer<typeof saveVaultLoginInputSchema>;

/** What the runtime types into the login form: the value and, with a seed, the current code. It
 * leaves the vault only for the agent runtime, never through a door. */
export type WebsiteLoginFill = {
  readonly secretId: string;
  readonly name: string;
  readonly origin: string;
  readonly username: string;
  readonly password: string;
  /** The one-time code for now, when the login has a seed. */
  readonly code: string | null;
};

/** One secret an agent may use, as `zaistev vault list` shows it (S27.15, D750): what to write in
 * its place and where it may go, never its value. A website login names its site instead. */
export const agentVaultSecretSchema = z.object({
  name: z.string(),
  kind: vaultSecretKindSchema,
  placeholder: z.string(),
  hosts: z.array(z.string()),
  site: z.string().nullable(),
});
export type AgentVaultSecret = z.infer<typeof agentVaultSecretSchema>;
export const agentVaultSecretsSchema = z.object({ secrets: z.array(agentVaultSecretSchema) });
export type AgentVaultSecrets = z.infer<typeof agentVaultSecretsSchema>;
/** `GET /api/agents/me/vault/secrets` takes nothing: the agent is its key. */
export const agentVaultSecretsInputSchema = z.object({});

export const listVaultSecretsInputSchema = z.object({});
export const listVaultSecretsOutputSchema = z.object({ secrets: z.array(vaultSecretSchema) });
export type ListVaultSecretsOutput = z.infer<typeof listVaultSecretsOutputSchema>;

export const deleteVaultSecretInputSchema = z.object({ id: z.string().min(1).max(64) });
export type DeleteVaultSecretInput = z.infer<typeof deleteVaultSecretInputSchema>;

export const okSchema = z.object({ ok: z.literal(true) });
export type Ok = z.infer<typeof okSchema>;

/** What the REST door reads before the service does: the body, as text (`body: "text"`). */
export const vaultResolveInputSchema = z.object({ body: z.string() });

/** Seam 3's body, as the egress proxy sends it. */
export const vaultResolveBodySchema = z.object({
  turnToken: z.string().min(1).max(4096),
  placeholder: vaultPlaceholderSchema,
  host: vaultHostSchema,
});
export type VaultResolveBody = z.infer<typeof vaultResolveBodySchema>;

/** Seam 3's four refusals; nothing more specific ever travels. */
export const VAULT_REFUSALS = [
  "not_allowed",
  "unknown_placeholder",
  "host_not_allowed",
  "turn_expired",
] as const;
export type VaultRefusal = (typeof VAULT_REFUSALS)[number];

/** The resolve door's request as it arrived: the bearer is read from its headers. */
export type VaultResolveCall = {
  /** Lower-cased names. */
  readonly headers: Readonly<Record<string, string>>;
  readonly body: string;
};

/** What the resolve door answers: the value, a refusal with its reason, a wrong bearer, or a
 * body that is not seam 3's. */
export type VaultResolveOutcome =
  | { readonly kind: "resolved"; readonly value: string }
  | { readonly kind: "refused"; readonly reason: VaultRefusal }
  | { readonly kind: "unauthorized" }
  | { readonly kind: "malformed" };

/** The most secrets one `zaistev vault run` asks for at once. */
export const MAX_RELEASE_NAMES = 10;

/** `POST /api/agents/me/vault/release`'s body (ADR-023 §4, D601): the secrets' names. The turn's
 * token travels in the `X-Zaistev-Turn` header, which the host relay adds in the VM. */
export const vaultReleaseInputSchema = z.object({
  body: z.object({ names: z.array(vaultSecretNameSchema).min(1).max(MAX_RELEASE_NAMES) }).strict(),
});

/** Each value released for this turn, by its name; for one child process's environment only. */
export const vaultReleaseSchema = z.object({
  values: z.array(z.object({ name: z.string(), value: z.string() })),
});
export type VaultRelease = z.infer<typeof vaultReleaseSchema>;
